/**
 * Pure helpers behind the Artifacts gallery — the JSX-free, DOM-free half so the
 * asset→lightbox mapping and swipe/next/prev index math are exhaustively
 * unit-tested without a browser. The reactive store (`stores/gallery.ts`) and the
 * `ArtifactsGallery` component are thin shells over these.
 */
import type { LightboxItem } from '@4f/ui';
import type { MediaAssetView } from '../../api/types.ts';

/**
 * Map a stored asset to a `Lightbox` item, or `null` when its `url` could not be
 * signed (the caller skips it rather than rendering a broken `<img>`). `kind`
 * collapses to the lightbox's image/video split.
 */
export function toLightboxItem(asset: MediaAssetView, alt: string): LightboxItem | null {
  if (!asset.url) {
    return null;
  }
  return { src: asset.url, alt, kind: asset.kind === 'video' ? 'video' : 'image' };
}

/** The index of `assetId` in the set, or `0` when absent (open at the start). */
export function startIndexForAsset(
  assets: readonly MediaAssetView[],
  assetId: string | undefined,
): number {
  if (!assetId) {
    return 0;
  }
  const index = assets.findIndex((asset) => asset.assetId === assetId);
  return index >= 0 ? index : 0;
}

/** Wrap-around next index (swipe/→). `0` for an empty set. */
export function nextIndex(index: number, length: number): number {
  return length <= 0 ? 0 : (index + 1) % length;
}

/** Wrap-around previous index (swipe/←). `0` for an empty set. */
export function prevIndex(index: number, length: number): number {
  return length <= 0 ? 0 : (index - 1 + length) % length;
}

/** Clamp an index into `[0, length)` (defensive when the set shrinks). */
export function clampIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.min(Math.max(index, 0), length - 1);
}

const EXTENSION_BY_TYPE: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
};

/** A stable download filename for an asset (content-type → extension, id as the name). */
export function downloadFileName(asset: MediaAssetView): string {
  const ext = EXTENSION_BY_TYPE[asset.contentType] ?? (asset.kind === 'video' ? 'mp4' : 'png');
  return `${asset.assetId}.${ext}`;
}
